import React from 'react';
import { Button, Menu, makeStyles, MenuItem, Typography } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { DefaultTheme } from 'themes/interfaces/default-theme.interfaces';
import { defaultTheme } from 'themes/default-theme';
import { useTranslation } from 'react-i18next';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { feVisualizationType } from 'modules/core/store/selectors';
import { SupportedCompanies } from 'config';

export interface IDropDownElement {
  id: string;
  name: string;
  nestingLevel?: number;
}

export interface IDropDownProps {
  elements: IDropDownElement[];
  onElementSelect: (element: IDropDownElement) => void;
  width?: number;
  transparent?: boolean;
  firstElementSelected: IDropDownElement;
  isSelected?: boolean;
  itemFontWeight?: number;
}

export type DropDownProps = React.PropsWithChildren<IDropDownProps>;

const makeClasses = makeStyles<DefaultTheme, { isOpen: boolean; menuWidth: number; transparent: boolean, showSelection: boolean, itemFontWeight: number }>(theme => ({
  button: {
    '&, &:hover': {
      ...defaultTheme.mixins.fonts.bold.xs,
      display: 'inline-flex',
      alignItems: 'center',
      height: theme.spacing(7),
      padding: theme.spacing(0, 1),
      // border: props => // TODO investigate if this feature is even needed
      //   !props.transparent
      //     ? `1px solid ${defaultTheme.palette.grey['400']}`
      //     : props.isOpen
      //     ? '1px solid transparent'
      //     : `1px solid ${theme.palette.common.white}`,
      borderBottomLeftRadius: props => (props.isOpen ? '0' : theme.spacing(0.5)),
      borderBottomRightRadius: props => (props.isOpen ? '0' : theme.spacing(0.5)),
      background: props => (props.isOpen || !props.transparent ? theme.palette.common.white : 'rgba(0,0,0,0.058)'),
      color: props => (props.isOpen || !props.transparent ? theme.palette.common.black : theme.palette.common.white),
      border: props => props.showSelection ? `3px solid ${theme.palette.primary.main}` : `1px solid ${defaultTheme.palette.grey['400']}`
    }
  },
  buttonSVG: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(3.5)
  },
  menuLabel: {
    textOverflow: 'ellipsis',
    maxWidth: '160px',
    fontWeight: 'bold',
    display: 'flex',
    flexDirection: "column",
    alignItems: "flex-start"
  },
  labelTitle: {
    ...defaultTheme.mixins.fonts.regular.xs,
  },
  menu: {
    minWidth: props => props.menuWidth,
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0'
  },
  list: {
    border: props => (!props.transparent ? `1px solid ${theme.palette.grey[300]}` : 'none'),
    padding: '0',
    '& li': {
      ...defaultTheme.mixins.fonts.bold.xs,
      display: 'flex',
      alignItems: 'center',
      height: theme.spacing(5),
      padding: theme.spacing(0, 3),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      fontWeight: props => props.itemFontWeight,
    }
  },
  nestedLevel: {
    '&::before': {
      padding: '0 5px',
      content: `"\\203A"`
    }
  }
}));

export const Dropdown: React.FC<DropDownProps> = (props: DropDownProps) => {
  const [menuWidth, setMenuWidth] = React.useState(0);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const visualizationType = useSelector(feVisualizationType)
  const isEXCompany = visualizationType === SupportedCompanies.EX;
  const [selectedElement, setSelectedElement] = React.useState<IDropDownElement>(props.firstElementSelected);
  const getButtonWidth = () => {
    if (buttonRef && buttonRef.current) {
      const width = buttonRef.current.offsetWidth;
      setMenuWidth(width);
    }
  };

  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const closeMenu = () => setAnchor(null);
  const isOpen = Boolean(anchor);

  const classes = makeClasses({
    isOpen,
    menuWidth,
    itemFontWeight: props.itemFontWeight ? props.itemFontWeight : 400,
    transparent: props.transparent === true ? true : false,
    showSelection: props.isSelected === true ? true : false
  });

  const onElementClick = (event: React.MouseEvent<HTMLElement>, element: IDropDownElement) => {
    setSelectedElement(element);
    closeMenu();
    props.onElementSelect(element);
  };

  const menuItem = (element: IDropDownElement) => (
    <MenuItem onClick={event => onElementClick(event, element)} key={element.id}>
      {(element.nestingLevel && element.nestingLevel > 0) ?
        <div className={classes.nestedLevel}>{element.name}</div>
       : element.name}
    </MenuItem>
  );

  const renderExpandIcon = React.useMemo(
    () => (
      isOpen ? <ExpandLess className={classes.buttonSVG} /> : <ExpandMore className={classes.buttonSVG} />
    ), [isOpen])

  return (
    <>
      <Button
        ref={buttonRef}
        classes={{ root: classes.button }}
        onClick={e => {
          getButtonWidth();
          setAnchor(e.currentTarget);
        }}
      >
        <Typography noWrap className={classes.menuLabel}>
          <span className={classes.labelTitle}>{t("common.round")}</span>
          {selectedElement.name}
        </Typography>
        {isEXCompany ? renderExpandIcon : <ArrowDropDownIcon className={classes.buttonSVG} />}
      </Button>
      <Menu
        elevation={0}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        getContentAnchorEl={null}
        marginThreshold={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        classes={{
          paper: classes.menu,
          list: classes.list
        }}
      >
        {props.elements.map((element: IDropDownElement) => menuItem(element))}
      </Menu>
    </>
  );
};
